// TB-080 (DEF-170): every toast goes through here.
// vue-toast-notification renders `message` with v-html, so server text could
// inject markup. The message is escaped and rendered as plain text, and a
// toast with no text (empty, whitespace, 'undefined', 'null') is not shown at
// all: that used to give an empty red box when an API error had no message.
import { useToast as baseUseToast } from 'vue-toast-notification'

const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', '\'': '&#39;' }

export function escapeHtml (value) {
  return String(value).replace(/[&<>"']/g, (c) => HTML_ESCAPES[c])
}

/**
 * Plain text for a toast, or '' when there is nothing to show.
 * Accepts what the API returns as `message`: a string, an array of strings
 * (class-validator errors, the first non-empty one is shown) or an object
 * with a `message`.
 */
export function toastText (message) {
  if (message === null || message === undefined) return ''
  if (Array.isArray(message)) {
    for (const m of message) {
      const t = toastText(m)
      if (t) return t
    }
    return ''
  }
  if (typeof message === 'object') return toastText(message.message)
  const text = String(message).trim()
  if (!text || text === 'undefined' || text === 'null' || text === '[object Object]') return ''
  return text
}

const NOOP = { dismiss () {} }
export const GENERIC_ERROR = 'Something went wrong. Please try again.'

/**
 * Wraps a vue-toast-notification instance in place. success / error / info /
 * warning / default all call this.open, so guarding open covers them all.
 */
export function wrapToast (instance) {
  if (!instance || typeof instance.open !== 'function' || instance.__tbSafe) return instance
  const rawOpen = instance.open
  instance.open = function (options) {
    const opts = typeof options === 'string' || options === null || options === undefined ? { message: options } : { ...options }
    let text = toastText(opts.message)
    // TB-080 review: an error with no usable text (network error, timeout,
    // empty 500) still tells the user something failed; other types stay quiet.
    if (!text && opts.type === 'error') text = GENERIC_ERROR
    if (!text) return NOOP
    opts.message = escapeHtml(text)
    return rawOpen.call(this, opts)
  }
  instance.__tbSafe = true
  return instance
}

// TB-110: same corner as the app-wide plugin (main.js). useToast() alone
// defaulted to bottom-right, which sat under the fold at 1440x900.
export const TOAST_DEFAULTS = { position: 'top-right' }

/** Drop-in for vue-toast-notification's useToast. */
export function useToast (props) {
  return wrapToast(baseUseToast({ ...TOAST_DEFAULTS, ...(props || {}) }))
}
